import React, { useState, useRef, useCallback } from 'react';

import { Link } from 'react-router-dom';

import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  EmailRule,
  StringLengthRule,
  CustomRule,
} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';

import logo from '../../../resources/logo.svg';
import { register, resendActivation } from '../../../api/registration';

import './RegistrationForm.scss';

export const RegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const formData = useRef({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    companyAddress: '',
    companyAddressNr: '',
    companyZipCode: '',
    companyCity: '',
    startTrial: true,
  });

  const confirmPassword = useCallback(
    (options: { value: string | number }) => String(options.value) === formData.current.password,
    []
  );

  const onSubmit = useCallback(async(e: React.FormEvent) => {
    e.preventDefault();
    const { confirmPassword: _omit, ...data } = formData.current;
    void _omit;
    setLoading(true);
    try {
      const result = await register(data);
      if (result.Success) {
        if (result.PaymentUrl) {
          // Redirect to Revolut checkout for payment
          notify('Account aangemaakt! U wordt doorgestuurd naar de betaalpagina...', 'success', 3000);
          setTimeout(() => {
            window.location.href = result.PaymentUrl!;
          }, 1500);
        } else {
          setSuccess(true);
          notify(result.Message, 'success', 4000);
        }
      } else {
        notify(result.Message || 'Registratie mislukt.', 'error', 3000);
      }
    } catch (err: unknown) {
      let msg = 'Registratie mislukt. Probeer het opnieuw.';
      if (
        typeof err === 'object' &&
        err !== null &&
        'response' in err
      ) {
        const axiosErr = err as { response?: { data?: string } };
        if (axiosErr.response?.data) {
          msg = axiosErr.response.data;
        }
      }
      notify(msg, 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  const onResendActivation = useCallback(async() => {
    const email = formData.current.email;
    if (!email) return;
    setResending(true);
    try {
      const result = await resendActivation(email);
      notify(result.message, 'success', 3000);
    } catch {
      notify('Kon activatie e-mail niet opnieuw verzenden.', 'error', 3000);
    } finally {
      setResending(false);
    }
  }, []);

  if (success) {
    return (
      <div className='reg-page'>
        <div className='reg-left'>
          <div className='reg-left-inner'>
            <img src={logo} alt='WerkbonIQ' className='reg-logo' />
            <div className='registration-success'>
              <div className='success-icon'>✓</div>
              <h3>Account aangemaakt!</h3>
              <p>
                We hebben een activatielink naar uw e-mailadres gestuurd.<br />
                Controleer uw inbox (en spam-map) om uw account te activeren.
              </p>
              <Link to='/login' className='dx-button dx-button-default dx-button-mode-contained success-btn'>
                <span className='dx-button-text'>Naar inloggen</span>
              </Link>
              <button
                type='button'
                className='resend-btn'
                onClick={onResendActivation}
                disabled={resending}
              >
                {resending ? 'Verzenden...' : 'Activatie e-mail opnieuw versturen'}
              </button>
            </div>
          </div>
        </div>
        <div className='reg-right'>
          <div className='reg-right-inner'>
            <h2>WerkbonIQ helpt je op weg met je digitale werkbon.</h2>
            <ul className='reg-features'>
              <li>Werkbonnen digitaal beheren</li>
              <li>Storingen & onderhoud bijhouden</li>
              <li>Klant- en locatiebeheer</li>
              <li>Rapportages & inzichten</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='reg-page'>
      {/* ── Left: form ── */}
      <div className='reg-left'>
        <div className='reg-left-inner'>
          <img src={logo} alt='WerkbonIQ' className='reg-logo' />
          <h1 className='reg-heading'>Hallo! Welkom bij WerkbonIQ</h1>
          <p className='reg-sub'>Start uw 14-daagse gratis proefperiode</p>
          <form onSubmit={onSubmit}>
            <Form
              formData={formData.current}
              disabled={loading}
              showColonAfterLabel={false}
              showRequiredMark={false}
              colCount={2}
            >
              <Item dataField='companyName' editorType='dxTextBox' editorOptions={companyNameOptions} colSpan={2}>
                <RequiredRule message='Bedrijfsnaam is vereist' />
                <Label text='Bedrijfsnaam' />
              </Item>
              <Item dataField='firstName' editorType='dxTextBox' editorOptions={textOptions}>
                <RequiredRule message='Voornaam is vereist' />
                <Label text='Voornaam' />
              </Item>
              <Item dataField='lastName' editorType='dxTextBox' editorOptions={textOptions}>
                <RequiredRule message='Achternaam is vereist' />
                <Label text='Achternaam' />
              </Item>
              <Item dataField='email' editorType='dxTextBox' editorOptions={emailOptions} colSpan={2}>
                <RequiredRule message='E-mailadres is vereist' />
                <EmailRule message='Voer een geldig e-mailadres in' />
                <Label text='E-mailadres' />
              </Item>
              <Item dataField='phone' editorType='dxTextBox' editorOptions={phoneOptions} colSpan={2}>
                <Label text='Telefoonnummer (optioneel)' />
              </Item>
              <Item dataField='companyAddress' editorType='dxTextBox' editorOptions={textOptions}>
                <Label text='Adres' />
              </Item>
              <Item dataField='companyAddressNr' editorType='dxTextBox' editorOptions={textOptions}>
                <Label text='Huisnummer' />
              </Item>
              <Item dataField='companyZipCode' editorType='dxTextBox' editorOptions={textOptions}>
                <Label text='Postcode' />
              </Item>
              <Item dataField='companyCity' editorType='dxTextBox' editorOptions={textOptions}>
                <Label text='Plaats' />
              </Item>
              <Item dataField='password' editorType='dxTextBox' editorOptions={passwordOptions}>
                <RequiredRule message='Wachtwoord is vereist' />
                <StringLengthRule min={8} message='Minimaal 8 tekens' />
                <Label text='Wachtwoord' />
              </Item>
              <Item dataField='confirmPassword' editorType='dxTextBox' editorOptions={confirmPasswordOptions}>
                <RequiredRule message='Bevestig uw wachtwoord' />
                <CustomRule message='Wachtwoorden komen niet overeen' validationCallback={confirmPassword} />
                <Label text='Wachtwoord bevestigen' />
              </Item>
              <Item dataField='startTrial' editorType='dxRadioGroup' editorOptions={trialOptions} colSpan={2}>
                <Label text='Hoe wilt u starten?' />
              </Item>
              <Item colSpan={2}>
                <div className='policy-info'>
                  Door een account aan te maken gaat u akkoord met de{' '}
                  <Link to='/login'>Algemene Voorwaarden</Link> en{' '}
                  <Link to='/login'>Privacybeleid</Link>.
                </div>
              </Item>
              <ButtonItem colSpan={2}>
                <ButtonOptions width='100%' type='default' useSubmitBehavior>
                  <span className='dx-button-text'>
                    {loading ? (
                      <LoadIndicator width='24px' height='24px' visible />
                    ) : (
                      'Doorgaan'
                    )}
                  </span>
                </ButtonOptions>
              </ButtonItem>
            </Form>
          </form>
          <div className='reg-footer'>
            <Link to='/login'>Breng me naar een bestaand account!</Link>
          </div>
        </div>
      </div>

      {/* ── Right: branding ── */}
      <div className='reg-right'>
        <div className='reg-right-inner'>
          <h2>WerkbonIQ helpt je op weg met je digitale werkbon.</h2>
          <ul className='reg-features'>
            <li>Werkbonnen digitaal beheren</li>
            <li>Storingen & onderhoud bijhouden</li>
            <li>Klant- en locatiebeheer</li>
            <li>Rapportages & inzichten</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const companyNameOptions = { stylingMode: 'outlined', placeholder: 'Uw bedrijfsnaam', height: 34 };
const textOptions = { stylingMode: 'outlined', height: 34 };
const emailOptions = { stylingMode: 'outlined', placeholder: 'naam@bedrijf.nl', mode: 'email', height: 34 };
const phoneOptions = { stylingMode: 'outlined', placeholder: '+31 6 12345678', height: 34 };
const passwordOptions = { stylingMode: 'outlined', placeholder: 'Minimaal 8 tekens', mode: 'password', height: 34 };
const confirmPasswordOptions = { stylingMode: 'outlined', placeholder: 'Herhaal wachtwoord', mode: 'password', height: 34 };
const trialOptions = {
  items: [
    { text: '14 dagen gratis proberen (geen betaalgegevens nodig)', value: true },
    { text: 'Direct betaalgegevens invullen', value: false },
  ],
  valueExpr: 'value',
  displayExpr: 'text',
  layout: 'vertical',
};
